import * as React from "react";

import { HeaderSubKind } from "./HeaderSubKind";
import { HorizontalAlign } from "./HorizontalAlign";
import { BodyCell, Col, HeaderCell, HeaderRow, HorizontalLineRow, Table, TextHackElement } from "./SimpleTable.styles";
import { VerticalAlign } from "./VerticalAlign";

const SimpleTableColumnCountContext = React.createContext<number>(1);

interface SimpleTableProps {
    children: React.ReactNode;
    columnCount: number;
}

export function SimpleTable(props: SimpleTableProps): JSX.Element {
    return (
        <SimpleTableColumnCountContext.Provider value={props.columnCount}>
            <Table>{props.children}</Table>
        </SimpleTableColumnCountContext.Provider>
    );
}

interface SimpleTableColgroupProps {
    children: React.ReactNode;
}

export function SimpleTableColgroup(props: SimpleTableColgroupProps): JSX.Element {
    return <colgroup>{props.children}</colgroup>;
}

interface SimpleTableColgroupColumnProps {
    children: React.ReactNode;
    width?: string | number;
    showRightBorder?: boolean;
}

export function SimpleTableColgroupColumn(props: SimpleTableColgroupColumnProps): JSX.Element {
    return (
        <Col borderRight={props.showRightBorder} style={{ width: props.width }}>
            {props.children}
        </Col>
    );
}

interface SimpleTableBodyRowProps {
    children: React.ReactNode;
}

export function SimpleTableBodyRow(props: SimpleTableBodyRowProps): JSX.Element {
    return <tr>{props.children}</tr>;
}

export function SimpleTableBodyHorizontalLineRow(): JSX.Element {
    return (
        <SimpleTableColumnCountContext.Consumer>
            {value => (
                <HorizontalLineRow>
                    <td colSpan={value}></td>
                </HorizontalLineRow>
            )}
        </SimpleTableColumnCountContext.Consumer>
    );
}

interface SimpleTableHeaderRowProps {
    children: React.ReactNode;
    className?: string;
    subkind?: HeaderSubKind;
    isLastTextHeaderRow?: boolean;
}

export function SimpleTableHeaderRow(props: SimpleTableHeaderRowProps): JSX.Element {
    return (
        <HeaderRow numbersRow={props.subkind === "numbers"} last={props.isLastTextHeaderRow}>
            {props.children}
        </HeaderRow>
    );
}

interface SimpleTableColumnProps {
    children: React.ReactNode;
    colSpan?: number;
    rowSpan?: number;
    empty?: boolean;
    className?: string;
    indent?: number;
    isGray?: boolean;
    horizontalAlign?: HorizontalAlign;
    align: VerticalAlign;
}

export class SimpleTableColumn extends React.PureComponent<SimpleTableColumnProps> {
    public render(): JSX.Element {
        return (
            <BodyCell
                colSpan={this.props.colSpan}
                rowSpan={this.props.rowSpan}
                indent={this.props.indent}
                empty={this.props.empty}
                isGray={this.props.isGray}
                horizontalAlign={this.props.horizontalAlign}
                verticalAlignSpec={this.props.align}>
                {this.props.align === "baseline" && <TextHackElement />}
                {this.props.children}
            </BodyCell>
        );
    }
}

interface SimpleTableHeaderColumnProps {
    children: React.ReactNode;
    colSpan?: number;
    rowSpan?: number;
    subKind?: HeaderSubKind;
    requiredEmpty?: boolean;
    isGray?: boolean;
    horizontalAlign?: HorizontalAlign;
    align: VerticalAlign;
}

export function SimpleTableHeaderColumn(props: SimpleTableHeaderColumnProps): JSX.Element {
    return (
        <HeaderCell
            numbersCell={props.subKind === "numbers"}
            isGray={props.isGray}
            empty={props.requiredEmpty}
            horizontalAlign={props.horizontalAlign}
            verticalAlignSpec={props.align}
            colSpan={props.colSpan}
            rowSpan={props.rowSpan}>
            {props.children}
        </HeaderCell>
    );
}

interface SimpleTableHeaderProps {
    children: React.ReactNode;
}

export function SimpleTableHeader(props: SimpleTableHeaderProps): JSX.Element {
    return <thead>{props.children}</thead>;
}

interface SimpleTableBodyProps {
    children: React.ReactNode;
}

export function SimpleTableBody(props: SimpleTableBodyProps): JSX.Element {
    return <tbody>{props.children}</tbody>;
}
